// ===================================================
// CONFIGURAÇÃO DA API - Detecção Automática de Ambiente
// ===================================================

/** Porta do backend em desenvolvimento local (rede ou localhost). */
function resolveLocalBackendPort() {
  if (
    typeof import.meta !== 'undefined' &&
    import.meta.env?.VITE_BACKEND_PORT
  ) {
    return String(import.meta.env.VITE_BACKEND_PORT)
  }
  if (typeof window !== 'undefined' && window.API_BACKEND_PORT) {
    return String(window.API_BACKEND_PORT)
  }
  // Padrão local: homolog (4001); produção local usa VITE_BACKEND_PORT=4000 ou dev:production
  return '4001'
}

/** Vite/npm run dev (8000, 8001, 5173…). IIS local usa porta 80/443 sem porta explícita. */
function isLocalDevServerPort(port) {
  if (!port || port === '80' || port === '443') return false
  return true
}

/** Portas típicas do Vite (dev/preview) — usam proxy /api no mesmo host. */
function isViteDevPort(port) {
  return ['8000', '8001', '5173', '5174', '4173'].includes(String(port || ''))
}

/** Verifica se o hostname é IP de rede local / Tailscale (tablet/totem na LAN). */
function isPrivateNetwork(host) {
  if (!host || host === 'localhost' || host === '127.0.0.1') return false
  const parts = String(host).split('.').map((p) => Number(p))
  if (
    parts.length !== 4 ||
    parts.some((n) => !Number.isInteger(n) || n < 0 || n > 255)
  ) {
    return false
  }
  // 10.0.0.0/8
  if (parts[0] === 10) return true
  // 172.16.0.0/12
  if (parts[0] === 172 && parts[1] >= 16 && parts[1] <= 31) return true
  // 192.168.0.0/16
  if (parts[0] === 192 && parts[1] === 168) return true
  // Tailscale / CGNAT 100.64.0.0/10
  if (parts[0] === 100 && parts[1] >= 64 && parts[1] <= 127) return true
  return false
}

/** Alinhado a getApiUrl (evita "Backend HOMOLOGAÇÃO" com ENVIRONMENT: production). */
function getEnvironmentLabel() {
  const hostname = window.location.hostname
  const port = window.location.port
  const fullUrl = window.location.href
  if (isPrivateNetwork(hostname)) return 'development'
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return isLocalDevServerPort(port)
      ? 'development (local backend)'
      : 'development'
  }
  if (hostname.includes('recebhomolog') || fullUrl.includes('recebhomolog'))
    return 'staging'
  if (hostname.includes('recebimento') || fullUrl.includes('recebimento'))
    return 'production'
  return 'production'
}

function getApiUrl() {
  const protocol = window.location.protocol
  const hostname = window.location.hostname
  const port = window.location.port
  const fullUrl = window.location.href

  console.log('🔧 [API CONFIG] Detectando ambiente automaticamente...')
  console.log('🔧 [API CONFIG] Protocol:', protocol)
  console.log('🔧 [API CONFIG] Hostname:', hostname)
  console.log('🔧 [API CONFIG] Port:', port)
  console.log('🔧 [API CONFIG] Full URL:', fullUrl)

  // Rede local: se o front roda no Vite (8000, 5173…), usar proxy /api do mesmo
  // host/porta — necessário quando o front está em HTTPS (câmera exige contexto seguro).
  if (isPrivateNetwork(hostname)) {
    if (isLocalDevServerPort(port)) {
      console.log(
        '🔧 [API CONFIG] Rede local + Vite dev → proxy /api (mesmo host/porta do front)'
      )
      return '/api'
    }
    const apiBase = `${protocol}//${hostname}:${resolveLocalBackendPort()}/api`
    console.log('🔧 [API CONFIG] Ambiente: Rede local → Backend no mesmo IP')
    console.log('🔧 [API CONFIG] Backend:', apiBase)
    return apiBase
  }

  // Desenvolvimento local (Vite: 8000, 8001 se 8000 ocupada, etc.)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    if (isLocalDevServerPort(port)) {
      if (isViteDevPort(port)) {
        console.log(
          `🔧 [API CONFIG] Vite (porta ${port}) → proxy /api no mesmo host`
        )
        return '/api'
      }
      const apiBase = `http://localhost:${resolveLocalBackendPort()}/api`
      console.log(
        `🔧 [API CONFIG] Desenvolvimento local (porta ${port}) → Backend ${apiBase}`
      )
      return apiBase
    }
    // IIS local com proxy reverso (porta 80/443)
    console.log('🔧 [API CONFIG] Ambiente: IIS local com proxy reverso')
    return '/api'
  }

  // Detecção automática baseada no domínio
  if (
    hostname.includes('recebhomolog.mercocamptech.com.br') ||
    fullUrl.includes('recebhomolog')
  ) {
    // Ambiente de homologação - backend na porta 4001
    console.log('🔧 [API CONFIG] Ambiente: HOMOLOGAÇÃO - Backend porta 4001')
    return '/api' // Proxy reverso configurado para redirecionar para porta 4001
  }

  if (
    hostname.includes('recebimento.mercocamptech.com.br') ||
    fullUrl.includes('recebimento')
  ) {
    // Ambiente de produção - backend na porta 4000
    console.log('🔧 [API CONFIG] Ambiente: PRODUÇÃO - Backend porta 4000')
    return '/api' // Proxy reverso configurado para redirecionar para porta 4000
  }

  // Fallback para produção
  console.log('🔧 [API CONFIG] Ambiente: FALLBACK (produção)')
  return '/api'
}

// Exportar BASE_URL para uso no main.js
export const BASE_URL = getApiUrl()

/**
 * URL base do serviço Java de exportação (PortalMercocampExports no Railway).
 * Configurável em config.js via window.EXPORTS_SERVICE_URL.
 * Em desenvolvimento local, cai em http://localhost:8080.
 */
function getExportsUrl() {
  if (typeof window !== 'undefined' && window.EXPORTS_SERVICE_URL) {
    return window.EXPORTS_SERVICE_URL
  }
  const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8080'
  }
  if (isPrivateNetwork(hostname)) {
    return `http://${hostname}:8080`
  }
  // Produção: configurar window.EXPORTS_SERVICE_URL em config.js
  return ''
}

export const EXPORTS_BASE_URL = getExportsUrl()

// Exportar API_CONFIG para compatibilidade com componentes que usam
export const API_CONFIG = {
  getApiUrl: getApiUrl,
  API_URL: BASE_URL,
  TIMEOUT: 60000, // 60s para bancos remotos/lentos
  RETRY_ATTEMPTS: 3,
  ENABLE_DEBUG: true,
  ENABLE_MOCK: false,
  ENVIRONMENT: getEnvironmentLabel(),
  VERSION: '1.0.0',
}

// Também disponibilizar no window para compatibilidade
if (typeof window !== 'undefined') {
  window.API_URL = BASE_URL
  window.API_CONFIG = API_CONFIG
}

console.log('========================================')
console.log('🔧 Configuração da API carregada:')
console.log('🔧 BASE_URL:', BASE_URL)
console.log('========================================')
